import { t } from '@framework/i18n/intl/i18next.ts';
import { type DefaultOptionType } from 'antd/es/select';
import { omit } from 'lodash';
import type React from 'react';
import { type KeyboardEvent, useCallback, useMemo, useState } from 'react';
import { useInputController } from '../hooks/useInputController.ts';
import { useFormItemScreenReaderLabelAddition } from '../item/label/useFormItemScreenReaderLabelAddition.tsx';
import { useFormItemAccessibility } from '../item/useFormItemAccessibility.tsx';
import { useFormItemEnabled } from '../item/useFormItemEnabled.ts';
import { getReactNodeText } from '../utils/getReactNodeText.ts';
import { renderSelectOptions } from './select/renderSelectOptions.tsx';
import { Select, type SelectProps } from './select/Select.tsx';
import { SelectReadOnly } from './select/SelectReadOnly.tsx';
import { useFilterSelectValuesOnOptionsChange } from './select/useFilterSelectValuesOnOptionsChange.ts';
import { useSelectedOptionLabel } from './select/useSelectedOptionLabel.tsx';

export type SearchOptions = Pick<
  SelectProps<unknown>,
  'showSearch' | 'optionFilterProp' | 'filterOption' | 'filterSort'
>;

export interface SelectOption {
  key?: string | number;
  disabled?: boolean;
  value?: string | number;
  title?: string;
  className?: string;
  style?: React.CSSProperties;
  label: React.ReactNode;
  /**
   * Attenzione, non deve mai contenere HTML non renderizzato.
   * In caso di aRTE, utilizzare `parseAdvancedRichTextToString` per processare
   * il testo _prima_ di impostarlo come `labelText`.
   */
  labelText?: string | null;
  /**
   * Campo aggiuntivo per il testo dell'opzione, utile per la ricerca
   * E per il rendering dell'accessibilità.
   * Non è obbligatorio, viene comunque usato il campo `optionFilterProp`
   */
  searchable?: string;
  options?: SelectOption[];
}

export interface ISelectInputProps<
  TOpt extends DefaultOptionType = DefaultOptionType,
> extends Omit<SelectProps, 'options' | 'name' | 'onChange'> {
  name: string;
  options?: SelectOption[] | null;
  renderOption?: (option: SelectOption) => React.ReactNode;
  children?: React.ReactNode;
  /**
   * Se True, renderizza le opzioni raggruppate
   */
  group?: boolean;
  /**
   * Disabilita l'ordinamento lessicografico dei **gruppi** di opzioni.
   * Le opzioni all'interno di un gruppo vengono sempre ordinate.
   */
  skipGroupSort?: boolean;
  /**
   * Disabilita l'ordinamento lessicografico delle opzioni durante la ricerca.
   */
  disableSearchSort?: boolean;
  /**
   * Permette di personalizzare la onChange della Select.
   * Utilizzare `onChange` per richiamare la onChange del Controller
   */
  onChange?: (
    value: any,
    option: TOpt | TOpt[] | undefined,
    onChange: (event: any) => void,
  ) => void;
  /**
   * Aggungiamo proprietà readOnly non gestita da antd
   */
  readOnly?: boolean;
  keepDisabledOptions?: boolean;
  skipResetOnMissingValue?: boolean;
  /**
   * in caso che dobbiamo trasformare il valore per l'input
   */
  transformValue?: (value: any) => any;
  searchable?: boolean;
}

export function SelectInput<TOpt extends DefaultOptionType = DefaultOptionType>(
  props: ISelectInputProps<TOpt>,
) {
  const {
    name,
    options,
    renderOption,
    children,
    onChange: onCustomChange,
    style,
    readOnly,
    group,
    keepDisabledOptions = false,
    skipResetOnMissingValue = false,
    loading,
    transformValue,
    searchable = false,
    skipGroupSort = false,
    disableSearchSort = false,
    ...inputProps
  } = props;

  const { enabled, visuallyDisabled } = useFormItemEnabled();

  const {
    field: { value, onChange, onBlur, ref },
  } = useInputController(name, { enabled });

  const accessibility = useFormItemAccessibility();

  const [search, setSearch] = useState<string | null>(null);
  const filterProp = inputProps.optionFilterProp ?? 'label';
  const selectedLabel = useSelectedOptionLabel(
    value,
    options,
    inputProps.mode,
    filterProp,
  );

  // Aggiungo un label nascosto per l'accessibilità, che indica il valore
  // selezionato e se ci sono opzioni o meno nella Select.
  const noResultsLabel =
    options != null &&
    options.length === 0 &&
    !loading &&
    t(
      'inputs.select.nessuna-opzione-disponibile',
      '(Nessuna opzione disponibile)',
    );

  const screenReaderLabelAddition =
    selectedLabel || noResultsLabel
      ? `${selectedLabel ?? ''} ${noResultsLabel || ''}`
      : undefined;

  useFormItemScreenReaderLabelAddition(
    readOnly ? null : screenReaderLabelAddition,
  );

  // Searchable logic
  const searchOptions: SearchOptions = useMemo(
    () =>
      searchable
        ? ({
            // Abilita la ricerca tramite uno stato _controllato_
            showSearch: true,
            searchValue: search,
            onSearch: value => {
              setSearch(value);
            },
            filterOption: (input, option) =>
              getReactNodeText(option?.[filterProp])
                .toLocaleLowerCase()
                .includes(input.toLocaleLowerCase()),
            filterSort: (a, b) => {
              if (skipGroupSort && a.options && b.options) return 0;
              if (disableSearchSort) return 0;

              return getReactNodeText(a?.[filterProp]).localeCompare(
                getReactNodeText(b?.[filterProp]).toLocaleLowerCase(),
              );
            },
          } as Partial<SelectProps>)
        : {},
    [searchable, search, filterProp, skipGroupSort],
  );

  // Gestione del backspace e delete per la cancellazione del valore,
  // per supportare l'accessibilità.
  // In caso non sia _searchable_, ci aspettiamo che il valore di
  // `search` sia comunque null (Falsy)
  const handleInputKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (
        (e.key === 'Delete' || e.key === 'Backspace') &&
        inputProps.allowClear &&
        !search // '' || null
      ) {
        if (searchable) setSearch('');
        onChange(null);
      }
    },
    [inputProps.allowClear, search, onChange, searchable],
  );

  const transformedValue = useMemo(
    () =>
      transformValue?.(value) ??
      (value == null && inputProps.mode != null ? [] : value),
    [inputProps.mode, transformValue, value],
  );

  // Nel caso cambino le options (e.g. async select) e il valore non
  // sia presente tra le opzioni disponibili, allora devo resettare il campo
  useFilterSelectValuesOnOptionsChange({
    mode: props.mode,
    // Se siamo ancora in caricamento delle opzioni, passiamo null in modo
    // da non resettare il campo
    // Anche se esternamente decidiamo di non resettare il campo, in caso di
    // opzioni "simulate" (e.g. async widget)
    options:
      Boolean(loading) || Boolean(skipResetOnMissingValue) ? null : options,
    value: transformedValue,
    onChange,
    withDisabled: keepDisabledOptions,
  });

  if (readOnly) {
    return (
      <SelectReadOnly
        value={value}
        options={options ?? []}
        filterProp={filterProp}
        size={inputProps.size}
        disabled={visuallyDisabled}
      />
    );
  }

  return (
    <Select
      size="large"
      style={{ textAlign: 'left', ...style }}
      disabled={visuallyDisabled}
      // Le Select non supportano aria-required, errore di validazione Axe
      {...omit(accessibility.attributes, ['aria-required'])}
      loading={loading}
      popupMatchSelectWidth={false}
      {...searchOptions}
      onInputKeyDown={handleInputKeyDown}
      {...inputProps}
      ref={ref}
      value={transformedValue}
      onChange={(rawValue, option) => {
        // Se il valore è undefined, allora lo imposto a null (https://react-hook-form.com/docs/useform/register)
        const value = rawValue ?? null;
        return onCustomChange
          ? onCustomChange(value, option as any, onChange)
          : onChange(value);
      }}
      onBlur={onBlur}
    >
      {renderSelectOptions(group, options, renderOption)}
      {children}
    </Select>
  );
}
